import React, { useState, useEffect } from 'react';
import EmptyState from '../../components/ui/EmptyState';
import { useToast } from '../../components/ui/Toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import UniversalNavbar from '../../components/ui/UniversalNavbar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import SubmissionDetailCard from './components/SubmissionDetailCard';
import submissionService from '../../services/submissionService';
import conferenceService from '../../services/conferenceService';
import { formatDate } from '../../utils/formatters';

const ConferenceSubmissionsOverview = () => {
  // Replaces alert(): non-blocking, styled, translatable, and errors
  // persist until dismissed instead of vanishing.
  const { success: toastSuccess, error: toastError } = useToast();
  const { t } = useTranslation('submissions');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const conferenceId = searchParams?.get('conferenceId');
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [conference, setConference] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [deletingSubmissionId, setDeletingSubmissionId] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/user-sign-in');
      return;
    }

    if (!conferenceId) {
      navigate('/user-dashboard');
      return;
    }

    loadSubmissions();
  }, [user, conferenceId]);

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      setError('');

      const [submissionsData, conferenceData] = await Promise.all([
        submissionService?.getUserSubmissions(conferenceId, user?.id),
        conferenceService?.getById(conferenceId)
      ]);

      setSubmissions(submissionsData || []);
      setConference(conferenceData);
    } catch (err) {
      console.error('Error loading submissions:', err);
      setError(err?.message || 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      'draft': 'bg-muted text-foreground',
      'submitted': 'bg-primary-subtle text-primary',
      'under_review': 'bg-warning-subtle text-warning',
      'accepted': 'bg-success-subtle text-success',
      'rejected': 'bg-error-subtle text-error',
      'revision_required': 'bg-accent-subtle text-accent'
    };
    return colors?.[status] || 'bg-muted text-foreground';
  };

  const handleDeleteSubmission = async (submissionId) => {
    if (!window.confirm('Delete this submission? This cannot be undone.')) return;

    setDeletingSubmissionId(submissionId);
    try {
      await submissionService?.delete(submissionId);
      setSubmissions(prev => prev?.filter(submission => submission?.id !== submissionId) || []);
    } catch (err) {
      console.error('Error deleting submission:', err);
      toastError(err?.message ? `Failed to delete submission: ${err.message}` : 'Failed to delete submission.');
    } finally {
      setDeletingSubmissionId(null);
    }
  };

  const filteredSubmissions = selectedStatus === 'all' 
    ? submissions 
    : submissions?.filter(sub => sub?.status === selectedStatus);

  const statusCounts = {
    all: submissions?.length || 0,
    submitted: submissions?.filter(s => s?.status === 'submitted')?.length || 0,
    under_review: submissions?.filter(s => s?.status === 'under_review')?.length || 0,
    accepted: submissions?.filter(s => s?.status === 'accepted')?.length || 0,
    rejected: submissions?.filter(s => s?.status === 'rejected')?.length || 0,
    revision_required: submissions?.filter(s => s?.status === 'revision_required')?.length || 0
  };

  return (
    <div className="min-h-screen bg-background">
      <UniversalNavbar />
      <main className="page-shell py-8">
        <div className="page-shell py-6 md:py-8">
          {/* Header */}
          <div className="mb-6">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/user-dashboard')}
              iconName="ArrowLeft"
              iconPosition="left"
              iconSize={16}
              className="mb-4"
            >
              {t('submissionsOverview.backToDashboard')}
            </Button>
            
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                  {t('submissionsOverview.title')}
                </h1>
                {conference && (
                  <p className="text-sm md:text-base text-muted-foreground">
                    {conference?.name}
                  </p>
                )}
              </div>
              
              <Button
                variant="default"
                onClick={() => navigate(`/author-submission-portal?conferenceId=${conferenceId}`)}
                iconName="Plus"
                iconPosition="left"
                iconSize={16}
              >
                {t('submissionsOverview.newSubmission')}
              </Button>
            </div>
          </div>

          {/* Status Filter Tabs */}
          <div className="bg-muted/40 rounded-lg border border-border p-4 mb-6">
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'all', label: t('submissionsOverview.filters.all'), count: statusCounts?.all },
                { key: 'submitted', label: t('submissionsOverview.filters.submitted'), count: statusCounts?.submitted },
                { key: 'under_review', label: t('submissionsOverview.filters.underReview'), count: statusCounts?.under_review },
                { key: 'accepted', label: t('submissionsOverview.filters.accepted'), count: statusCounts?.accepted },
                { key: 'rejected', label: t('submissionsOverview.filters.rejected'), count: statusCounts?.rejected },
                { key: 'revision_required', label: t('submissionsOverview.filters.revisionRequired'), count: statusCounts?.revision_required }
              ]?.map(({ key, label, count }) => (
                <button
                  key={key}
                  onClick={() => setSelectedStatus(key)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    selectedStatus === key
                      ? 'bg-card text-foreground shadow-md border border-border'
                      : 'bg-transparent text-muted-foreground hover:bg-card/60 hover:text-foreground'
                  }`}
                >
                  {label} ({count})
                </button>
              ))}
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Icon name="Loader" size={32} className="animate-spin text-primary mx-auto mb-4" />
                <p className="text-muted-foreground">{t('submissionsOverview.loading')}</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-error mb-1">{t('submissionsOverview.errorTitle')}</h3>
                  <p className="text-sm text-error/80">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && filteredSubmissions?.length === 0 && (
            <div className="bg-card rounded-lg border border-border">
              <EmptyState
                filtered={selectedStatus !== 'all'}
                onClear={() => setSelectedStatus('all')}
                icon="FileText"
                title={selectedStatus === 'all'
                  ? t('submissionsOverview.emptyState.noSubmissions')
                  : t('submissionsOverview.emptyState.noStatusSubmissions', { status: selectedStatus?.replace('_', ' ') })}
                body={selectedStatus === 'all'
                  ? t('submissionsOverview.emptyState.noSubmissionsMessage')
                  : t('submissionsOverview.emptyState.noStatusMessage')}
                action={selectedStatus === 'all'
                  ? { label: t('submissionsOverview.emptyState.submitFirstPaper'), icon: 'Plus',
                      onClick: () => navigate(`/author-submission-portal?conferenceId=${conferenceId}`) }
                  : undefined}
              />
            </div>
          )}

          {/* Submissions List */}
          {!loading && !error && filteredSubmissions?.length > 0 && (
            <div className="space-y-4">
              {filteredSubmissions?.map((submission) => (
                <SubmissionDetailCard
                  key={submission?.id}
                  submission={submission}
                  getStatusBadgeColor={getStatusBadgeColor}
                  formatDate={formatDate}
                  onDelete={handleDeleteSubmission}
                  deleting={deletingSubmissionId === submission?.id}
                />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ConferenceSubmissionsOverview;
